import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonModal,
  IonRow,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonToolbar,
} from "@ionic/react";
import "driver.js/dist/driver.css";
import { chevronExpand, closeOutline, informationCircle } from "ionicons/icons";
import PubSub from "pubsub-js";
import React, { createRef, useContext, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import optionsGridConfig from "./optionsGridConfig";
import { UserPreferencesContext } from "../../../components/contexts/UserPreferencesContext";
import OneLoader from "../../../utils/component-utils/Loader/OneLoader";
import { send } from "../../../utils/sockets/asyncSocket";
import { OrdersType } from "../../../Constants";
import { CMEDfirebaseLogEvent } from "../../../utils/firebaseAnalytics/firebaseUtils";
import firebaseEventConfig from "../../../utils/firebaseAnalytics/firebaseEventConfig";
import Skeleton__OptionsGridForm from "./skeletons/Skeleton__OptionsGridForm";
import { OPTION_CATEGORY_CONFIG } from "./optionsGridConfig/optionCategoryConfig";

/**
 * @author
 * E45597 Aryamitra Chaudhuri
 */

type OptionsGridFormProps = {
  setgreekType: Function;
  greekType: any;
  selectedOptionCheckboxes: any;
  setSelectedOptionCategoryCheckboxes: any;
  selectedExpiryId: any;
  setSelectedExpiryId: any;
  returnToOptionGrid: () => void;
  hasSelectedOptions: boolean;
  setOptionsListData: (payload: { payload: any }) => void;
  setIsOptionsGridSearchModalOpen: (val: boolean) => void;
};

const OptionsGridForm: React.FC<OptionsGridFormProps> = ({
  greekType,
  setgreekType,
  selectedExpiryId,
  setSelectedExpiryId,
  selectedOptionCheckboxes,
  setSelectedOptionCategoryCheckboxes,
  returnToOptionGrid,
  hasSelectedOptions,
  setOptionsListData,
  setIsOptionsGridSearchModalOpen,
}) => {
  const [underlyingFutureData, setUnderlyingFutureData] = useState<{
    payload: any;
    loadingState: boolean;
    error: string;
  }>({ payload: [], loadingState: true, error: "" });
  const [expiryData, setExpiryData] = useState<{
    payload: any;
    loadingState: boolean;
    error: string;
  }>({ payload: [], loadingState: true, error: "" });
  const [updatedUserPrefData, setUpdatedUserPrefData] = useState(false);
  const [isUnderlyingFutureModalOpen, setIsUnderlyingFutureModalOpen] =
    useState(false);
  const [searchVal, setSearchVal] = useState("");
  const { userPreferences, setUserPreferences } = useContext(
    UserPreferencesContext,
  );
  const [
    selectedUnderlyingInstrumentName,
    setSelectedUnderlyingInstrumentName,
  ] = useState("");
  const [selectedUnderlyingInstrumentId, setSelectedUnderlyingInstrumentId] =
    useState("");
  const [optionCategoryData, setOptionCategoryData] = useState<{
    payload: any;
    loadingState: boolean;
    error: string;
  }>({ payload: [], loadingState: false, error: "" });

  const searchRef = createRef<HTMLIonSearchbarElement>();

  useEffect(() => {
    setUserPreferences(userPreferences);
  }, [updatedUserPrefData]);

  useEffect(() => {
    setTimeout(() => {
      isUnderlyingFutureModalOpen && searchRef.current?.setFocus();
    }, 750);
  }, [isUnderlyingFutureModalOpen]);

  const handleCheckboxChange = (event: any): void => {
    const value: string = event.target.value;
    const isChecked: boolean = event.target.checked;

    if (isChecked) {
      setSelectedOptionCategoryCheckboxes((prevState: any) => [
        ...prevState,
        value,
      ]);
    } else {
      setSelectedOptionCategoryCheckboxes((prevState: any) =>
        prevState.filter((item: any) => item !== value),
      );
    }
  };

  const getVisibleAndSortedCategories = (payload: any): string[] => {
    if (!payload) return [];
    const keys = Object.keys(payload);
    return keys.sort((a, b) => {
      const orderA = OPTION_CATEGORY_CONFIG[a]
        ? OPTION_CATEGORY_CONFIG[a].order
        : -1;
      const orderB = OPTION_CATEGORY_CONFIG[b]
        ? OPTION_CATEGORY_CONFIG[b].order
        : -1;
      return orderA - orderB;
    });
  };

  const visibleCategories = getVisibleAndSortedCategories(
    optionCategoryData?.payload,
  );

  //<------ REQUEST ACTION CALLERS ------->
  /**
   * @description
   * Action functions to perform options grid scenarios.
   */

  function reqUnderlyingFuture() {
    let request = {
      action: optionsGridConfig.socket.actions.CMED_UNDERLYING,
      requestID: uuidv4(),
      type: OrdersType.UNDERLYING,
      payload: {},
    };
    send(optionsGridConfig.socket.channels.TRADING, request);
  }

  function reqExpiry() {
    let request = {
      action: optionsGridConfig.socket.actions.CMED_OPTIONSGRID_EXPIRY,
      requestID: uuidv4(),
      type: OrdersType.OPTIONS_GRID_EXPIRY,
      payload: {},
    };
    send(optionsGridConfig.socket.channels.TRADING, request);
  }

  function reqOptionCategory() {
    let request = {
      action: optionsGridConfig.socket.actions.CMED_OPTIONSGRID_FIND_OPTIONS,
      requestID: uuidv4(),
      type: OrdersType.FIND_OPTIONS,
      payload: {
        underlyingInstrumentId: selectedUnderlyingInstrumentId,
        expiryId: selectedExpiryId,
      },
    };
    send(optionsGridConfig.socket.channels.TRADING, request);
    setOptionCategoryData({
      payload: [],
      loadingState: true,
      error: "",
    });
    setSelectedOptionCategoryCheckboxes([]);
  }

  useEffect(() => {
    if (
      selectedExpiryId !== undefined &&
      selectedUnderlyingInstrumentId?.length > 0
    ) {
      reqOptionCategory();
    }
  }, [selectedExpiryId, selectedUnderlyingInstrumentId]);

  //<------ PUBSUB SUBSCRIPTION DATA ------->
  /**
   * @description
   * Action functions to get data for options grid scenario.
   */

  const resUnderlyingFutureSuccess = (msg: any, response: any) => {
    setUnderlyingFutureData({
      payload: response?.payload,
      loadingState: false,
      error: "",
    });
  };

  const resUnderlyingFutureFailure = (msg: any, response: any) => {
    setUnderlyingFutureData({
      payload: [],
      loadingState: false,
      error: response?.payload,
    });
  };

  const resExpirySuccess = (msg: any, response: any) => {
    setExpiryData({
      payload: response?.payload,
      loadingState: false,
      error: "",
    });
  };

  const resExpiryFailure = (msg: any, response: any) => {
    setExpiryData({
      payload: [],
      loadingState: false,
      error: response?.payload,
    });
  };

  const resOptionCategorySuccess = (msg: any, response: any) => {
    setOptionCategoryData({
      payload: response?.payload,
      loadingState: false,
      error: "",
    });
  };

  useEffect(() => {
    const pubSubTokens = [
      PubSub.subscribe(
        optionsGridConfig.socketKeys.CMED_UNDERLYING_SUCCESS,
        resUnderlyingFutureSuccess,
      ),
      PubSub.subscribe(
        optionsGridConfig.socketKeys.CMED_OPTIONS_ERROR,
        resUnderlyingFutureFailure,
      ),
      PubSub.subscribe(
        optionsGridConfig.socketKeys.CMED_EXPIRY_SUCCESS,
        resExpirySuccess,
      ),
      PubSub.subscribe(
        optionsGridConfig.socketKeys.CMED_OPTIONS_ERROR,
        resExpiryFailure,
      ),
      PubSub.subscribe(
        optionsGridConfig.socketKeys.CMED_FIND_OPTIONS_SUCCESS,
        resOptionCategorySuccess,
      ),
    ];
    reqUnderlyingFuture();
    reqExpiry();
    return () => {
      pubSubTokens.forEach((token) => PubSub.unsubscribe(token));
    };
  }, []);

  function filterUnderlyingFuturesData(data: any, searchString: any) {
    if (
      searchString === "" ||
      searchString === undefined ||
      searchString === null
    )
      return data;
    return data
      ?.filter((item: any) =>
        item?.c?.toLowerCase().startsWith(searchString.toLocaleLowerCase()),
      )
      .concat(
        data?.filter(
          (item: any) =>
            !item?.c
              ?.toLowerCase()
              .startsWith(searchString.toLocaleLowerCase()) &&
            item?.c?.toLowerCase().includes(searchString.toLocaleLowerCase()),
        ),
      )
      .concat(
        data?.filter(
          (item: any) =>
            !item?.c
              ?.toLowerCase()
              .includes(searchString.toLocaleLowerCase()) &&
            (item?.id
              ?.toLowerCase()
              .includes(" " + searchString.toLocaleLowerCase()) ||
              item?.id
                ?.toLowerCase()
                .includes("/" + searchString.toLocaleLowerCase()) ||
              item?.id
                ?.toLowerCase()
                .includes("." + searchString.toLocaleLowerCase()) ||
              item?.id
                ?.toLowerCase()
                .includes("(" + searchString.toLocaleLowerCase()) ||
              item?.id
                ?.toLowerCase()
                .includes("-" + searchString.toLocaleLowerCase()) ||
              item?.id
                ?.toLowerCase()
                .includes("," + searchString.toLocaleLowerCase()) ||
              item?.id
                ?.toLowerCase()
                .indexOf(searchString.toLocaleLowerCase()) === 0),
        ),
      )
      .concat(
        data?.filter(
          (item: any) =>
            !item?.c
              ?.toLowerCase()
              .includes(searchString.toLocaleLowerCase()) &&
            !item?.id
              ?.toLowerCase()
              .includes(searchString.toLocaleLowerCase()) &&
            (item?.n
              ?.toLowerCase()
              .includes(" " + searchString.toLocaleLowerCase()) ||
              item?.n
                ?.toLowerCase()
                .includes("/" + searchString.toLocaleLowerCase()) ||
              item?.n
                ?.toLowerCase()
                .includes("." + searchString.toLocaleLowerCase()) ||
              item?.n
                ?.toLowerCase()
                .includes("(" + searchString.toLocaleLowerCase()) ||
              item?.n
                ?.toLowerCase()
                .includes("-" + searchString.toLocaleLowerCase()) ||
              item?.n
                ?.toLowerCase()
                .includes("," + searchString.toLocaleLowerCase()) ||
              item?.n
                ?.toLowerCase()
                .indexOf(searchString.toLocaleLowerCase()) === 0),
        ),
      );
  }

  interface OriginalData {
    [key: string]: any[];
  }

  function filterData(
    originalData: OriginalData,
    categoriesToFilter: string[],
  ): OriginalData {
    const filteredData: OriginalData = {};

    categoriesToFilter.forEach((category) => {
      if (originalData.hasOwnProperty(category)) {
        filteredData[category] = originalData[category];
      }
    });

    return filteredData;
  }

  const handleFilterOptionsList = () => {
    if (selectedOptionCheckboxes?.length === 0) {
      setOptionsListData({
        payload: filterData(optionCategoryData?.payload, visibleCategories),
      });
    } else {
      setOptionsListData({
        payload: filterData(
          optionCategoryData?.payload,
          selectedOptionCheckboxes,
        ),
      });
    }
  };

  return (
    <>
      {hasSelectedOptions ? (
        <IonToolbar>
          <IonButtons slot="end">
            <IonButton onClick={returnToOptionGrid}>
              <IonIcon
                icon={closeOutline}
                color="dark"
                slot="icon-only"
              ></IonIcon>
            </IonButton>
          </IonButtons>
        </IonToolbar>
      ) : null}
      {underlyingFutureData?.loadingState || expiryData?.loadingState ? (
        <Skeleton__OptionsGridForm />
      ) : (
        <>
          <IonContent>
            <div className="first-options-grid-screen">
              {/* ^_^ Underlying Future Selection Section ^_^ */}
              <IonList inset={true}>
                <IonItem
                  id="underlyingFuture"
                  lines="none"
                  color="light"
                  button
                  detail={false}
                  disabled={underlyingFutureData?.loadingState}
                  onClick={() => setIsUnderlyingFutureModalOpen(true)}
                >
                  <IonLabel slot="start">Underlying Future</IonLabel>
                  <IonLabel
                    slot="end"
                    style={
                      !selectedUnderlyingInstrumentName
                        ? {
                            opacity: 0.6,
                            marginRight: 3,
                          }
                        : {
                            opacity: 1,
                            marginRight: 3,
                          }
                    }
                  >
                    {!selectedUnderlyingInstrumentName
                      ? "Select"
                      : selectedUnderlyingInstrumentName}
                  </IonLabel>
                  {!selectedUnderlyingInstrumentName && (
                    <IonIcon
                      icon={chevronExpand}
                      slot="end"
                      size="small"
                      style={{ marginLeft: 0 }}
                    ></IonIcon>
                  )}
                </IonItem>
              </IonList>
              {/* ^_^ Expiry Selection Section ^_^ */}
              <IonList inset={true}>
                <IonItem lines="none" color="light">
                  <IonSelect
                    id="expiry"
                    label="Expiry"
                    placeholder="Select"
                    interface="action-sheet"
                    disabled={expiryData?.loadingState}
                    onIonChange={(e: any) =>
                      setSelectedExpiryId(e.detail.value)
                    }
                    value={selectedExpiryId}
                  >
                    {expiryData?.payload?.map((elem: any, index: any) => (
                      <IonSelectOption value={elem?.value} key={index}>
                        {elem?.text}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </IonItem>
              </IonList>
              {/* ^_^ Greeks Selection Section ^_^ */}
              <IonList inset={true}>
                <IonItem lines="none" color="light">
                  <IonSelect
                    label="Greeks Source"
                    placeholder="Select"
                    interface="action-sheet"
                    onIonChange={(e: any) => setgreekType(e.detail.value)}
                    value={greekType}
                  >
                    <IonSelectOption value="1">
                      Market Implied Vol
                    </IonSelectOption>
                    <IonSelectOption value="4">QuikStrike Vol</IonSelectOption>
                  </IonSelect>
                </IonItem>
              </IonList>
              {/* ^_^ Option Category Selection Section ^_^ */}
              {selectedExpiryId !== undefined &&
                selectedUnderlyingInstrumentId?.length > 0 && (
                  <IonListHeader>Option Category</IonListHeader>
                )}
              {optionCategoryData?.loadingState ? (
                <OneLoader loadingText="Loading Categories..." />
              ) : (
                <>
                  {visibleCategories.length === 0 &&
                  selectedExpiryId !== undefined &&
                  selectedUnderlyingInstrumentId?.length > 0 ? (
                    <IonList inset={true}>
                      <IonItem lines="none" color="warning">
                        <IonIcon
                          icon={informationCircle}
                          slot="start"
                        ></IonIcon>
                        <IonLabel>No options were found.</IonLabel>
                      </IonItem>
                    </IonList>
                  ) : (
                    <IonList inset={true}>
                      {visibleCategories.map((elem: any, index: any) => (
                        <IonItem color="light" key={index}>
                          <IonCheckbox
                            onIonChange={handleCheckboxChange}
                            value={elem}
                            checked={selectedOptionCheckboxes.includes(elem)}
                            labelPlacement="end"
                            mode="md"
                            justify="start"
                          >
                            {elem}
                          </IonCheckbox>
                        </IonItem>
                      ))}
                    </IonList>
                  )}
                </>
              )}
              <IonModal isOpen={isUnderlyingFutureModalOpen}>
                <IonHeader className="ion-no-border">
                  <IonToolbar>
                    <IonButtons slot="end">
                      <IonButton
                        onClick={() => {
                          setIsUnderlyingFutureModalOpen(false);
                          setSearchVal("");
                        }}
                      >
                        <IonIcon
                          icon={closeOutline}
                          color="dark"
                          slot="icon-only"
                        ></IonIcon>
                      </IonButton>
                    </IonButtons>
                  </IonToolbar>
                  <IonToolbar>
                    <IonSearchbar
                      placeholder="Search Underlying Future..."
                      onIonInput={(event: any) =>
                        setSearchVal(event.detail.value)
                      }
                      ref={searchRef}
                    ></IonSearchbar>
                  </IonToolbar>
                </IonHeader>
                <IonContent>
                  {filterUnderlyingFuturesData(
                    underlyingFutureData?.payload,
                    searchVal,
                  )?.length === 0 && (
                    <IonList inset={true}>
                      <IonItem lines="none" color="warning">
                        <IonIcon
                          icon={informationCircle}
                          slot="start"
                        ></IonIcon>
                        <IonLabel>No search result.</IonLabel>
                        <IonButton
                          onClick={() => {
                            reqUnderlyingFuture();
                            reqExpiry();
                          }}
                        >
                          Retry
                        </IonButton>
                      </IonItem>
                    </IonList>
                  )}
                  {filterUnderlyingFuturesData(
                    underlyingFutureData?.payload,
                    searchVal,
                  ).map((elem: any, index: any) => (
                    <IonItem
                      button
                      lines="full"
                      className={index % 2 === 0 ? "even_item_bg" : ""}
                      key={index}
                    >
                      <IonGrid>
                        <IonRow
                          onClick={() => {
                            setSelectedUnderlyingInstrumentId(elem?.id);
                            setSelectedUnderlyingInstrumentName(elem?.n);
                            setSearchVal("");
                            setIsUnderlyingFutureModalOpen(false);
                          }}
                        >
                          <IonCol>{elem?.c}</IonCol>
                          <IonCol>{elem?.n}</IonCol>
                        </IonRow>
                      </IonGrid>
                    </IonItem>
                  ))}
                </IonContent>
              </IonModal>
            </div>
          </IonContent>
          <IonFooter className="ion-padding">
            <IonButton
              id="searchOptions"
              expand="block"
              className="custom_cmed_mobile_button"
              onClick={() => {
                setIsOptionsGridSearchModalOpen(true);
                handleFilterOptionsList();
                CMEDfirebaseLogEvent(
                  firebaseEventConfig.CMEDComponentNames.CMED_OPTIONS_GRID,
                  firebaseEventConfig.eventNames.CMED_OPTIONS_GRID
                    .CMED_OPTIONS_GRID_INSTRUMENT_SEARCH,
                );
              }}
              disabled={
                selectedExpiryId === undefined ||
                selectedUnderlyingInstrumentId?.length === 0 ||
                visibleCategories.length === 0 ||
                greekType === undefined
              }
            >
              Search
            </IonButton>
          </IonFooter>
        </>
      )}
    </>
  );
};

export default OptionsGridForm;
